import { Send, Sparkles, Square, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../lib/cn.js';

export interface ChatInputBarProps {
  readonly disabled?: boolean;
  readonly isStreaming: boolean;
  readonly onSend: (text: string) => void;
  readonly onClear: () => void;
  readonly onStop: () => void;
}

export function ChatInputBar({
  disabled,
  isStreaming,
  onSend,
  onClear,
  onStop,
}: ChatInputBarProps): JSX.Element {
  const [input, setInput] = useState('');

  const submit = (): void => {
    const text = input.trim();
    if (!text || isStreaming || disabled) return;
    onSend(text);
    setInput('');
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex flex-col gap-2 border-t border-dashed border-border-subtle bg-deep/60 px-3 py-3"
    >
      <div className="flex items-end gap-2 rounded-xl border border-border-subtle bg-surface/80 px-3 py-2 transition-all focus-within:border-accent/40 focus-within:ring-2 focus-within:ring-accent/20">
        <Sparkles
          className={cn(
            'mt-1 h-4 w-4 flex-none transition-colors',
            isStreaming ? 'animate-pulse text-accent' : 'text-text-muted',
          )}
        />
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={
            disabled ? 'Open a repo to start chatting…' : 'Ask CodeGraph about this repository…'
          }
          rows={2}
          disabled={disabled}
          className="flex-1 resize-none bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none disabled:cursor-not-allowed"
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface px-2.5 py-1 text-[11px] text-text-secondary transition-colors hover:border-border-default hover:bg-hover hover:text-text-primary"
        >
          <Trash2 className="h-3 w-3" />
          Clear
        </button>
        <span className="font-mono text-[10px] text-text-muted">⏎ send · ⇧⏎ newline</span>
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/15 px-3 py-1.5 text-[11px] font-medium text-rose-300 transition-colors hover:border-rose-500/60 hover:bg-rose-500/25"
          >
            <Square className="h-3 w-3" />
            Stop
          </button>
        ) : (
          <button
            type="submit"
            disabled={!input.trim() || disabled}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-accent to-accent-dim px-3 py-1.5 text-[11px] font-semibold text-white shadow-glow transition-all',
              !input.trim() || disabled
                ? 'cursor-not-allowed opacity-40'
                : 'hover:-translate-y-0.5 hover:shadow-lg',
            )}
          >
            <Send className="h-3 w-3" />
            Send
          </button>
        )}
      </div>
    </form>
  );
}
